<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <validation-observer v-slot="{ handleSubmit }">
        <b-form
          class="auth-login-form mt-2"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <!-- Employee ID -->
          <b-form-group
            :label="$t('workerid')"
            label-for="empid"
            class="input-text"
          >
            <validation-provider
              v-slot="{ errors }"
              name="empid"
              :rules="{ required: true }"
            >
              <b-form-input
                id="empid"
                v-model="form.empid"
                maxlength="8"
                :state="errors.length ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Username -->
          <b-form-group
            :label="$t('user')"
            label-for="username"
            class="input-text"
          >
            <validation-provider
              v-slot="{ errors }"
              name="username"
              :rules="{ required: true }"
            >
              <b-form-input
                id="username"
                v-model="form.username"
                maxlength="50"
                :state="errors.length ? false : null"
                placeholder="john hammer"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Email -->
          <b-form-group
            :label="$t('email')"
            label-for="email"
            class="input-text"
          >
            <validation-provider
              v-slot="{ errors }"
              name="email"
              :rules="{ required: true, email: true }"
            >
              <b-form-input
                id="email"
                v-model="form.email"
                maxlength="50"
                :state="errors.length ? false : null"
                placeholder="john@example.com"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Password -->
          <b-form-group
            :label="$t('password')"
            label-for="password"
            class="input-text"
          >
            <validation-provider
              v-slot="{ errors }"
              name="password"
              :rules="{ required: !isEditing, min: 6 }"
            >
              <b-input-group
                class="input-group-merge"
                :class="errors.length ? 'is-invalid' : null"
              >
                <b-form-input
                  id="password"
                  v-model="form.password"
                  :state="errors.length ? false : null"
                  :type="passwordFieldType"
                  placeholder="••••••••"
                />
                <b-input-group-append is-text>
                  <feather-icon
                    class="cursor-pointer"
                    :icon="passwordToggleIcon"
                    @click="togglePasswordVisibility"
                  />
                </b-input-group-append>
              </b-input-group>
              <small class="text-danger">
                <template v-if="errors[0]">
                  Password must be at least 6 characters long
                </template>
              </small>
            </validation-provider>
          </b-form-group>

          <!-- User Role -->
          <b-form-group
            :label="$t('role')"
            label-for="role"
            class="input-text"
          >
            <validation-provider
              v-slot="{ errors }"
              name="role"
              :rules="{ required: true }"
            >
              <b-form-select
                id="role"
                v-model="form.role"
                :state="errors.length ? false : null"
                :options="roles.map(role => ({ value: role.id, text: role.name }))"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Department -->
          <b-form-group
            :label="$t('deptname')"
            label-for="dept"
            class="input-text"
          >
            <validation-provider
              v-slot="{ errors }"
              name="dept"
              :rules="{ required: true }"
            >
              <b-form-select
                id="dept"
                v-model="form.dept"
                :state="errors.length ? false : null"
                :select-size="3"
                :options="depts.map(dept => ({ value: dept.id, text: dept.dept }))"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- jobtitle -->
          <b-form-group
            :label="$t('jobtitle')"
            label-for="jobtitle"
            class="input-text"
          >
            <validation-provider
              v-slot="{ errors }"
              name="jobtitle"
              :rules="{ required: true }"
            >
              <b-form-select
                id="jobtitle"
                v-model="form.jobtitle"
                :state="errors.length ? false : null"
                :select-size="3"
                :options="jobtitles.map(jobtitle => ({ value: jobtitle.id, text: jobtitle.titleName }))"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Phone Number -->
          <b-form-group
            :label="$t('phone')"
            label-for="phone"
            class="input-text"
          >
            <validation-provider
              v-slot="{ errors }"
              name="phone"
            >
              <b-form-input
                id="phone"
                v-model="form.phone"
                maxlength="20"
                :state="errors.length ? false : null"
                placeholder="0810000000 / 888"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Profile Image Upload -->
          <b-form-group
            :label="$t('uploadimage')"
            label-for="profile-image"
          >
            <input
              type="file"
              accept="image/*"
              @change="onFileChange"
            ><br>
            <img
              v-if="imageSrc"
              :src="imageSrc"
              alt="Profile Image"
              style="margin-top: 10px; width: 100px; height: 100px; object-fit: cover;"
            >
          </b-form-group>

          <b-form-group
            :label="$t('drawsignature')"
            label-for="signature-canvas"
          >
            <canvas
              ref="signatureCanvas"
              style="border: 1px solid #000; background-color: rgba(255, 255, 255, 0); touch-action: none;"
              @mousedown="startDrawing"
              @mousemove="draw"
              @mouseup="stopDrawing"
              @mouseleave="stopDrawing"
              @touchstart.prevent="startDrawing"
              @touchmove.prevent="draw"
              @touchend="stopDrawing"
            /><br>
            <button
              class="button-custom"
              @click="clearCanvas"
            >
              {{ $t('clearsignature') }}
            </button>
          </b-form-group>

          <!-- Submit Button -->
          <b-button
            type="submit"
            variant="primary"
          >
            {{ isEditing ? $t('update') : $t('newadd') }}
          </b-button>
        </b-form>
      </validation-observer>
    </b-row>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BRow, BFormGroup, BFormInput, BInputGroupAppend, BInputGroup, BForm, BButton, BFormSelect,
} from 'bootstrap-vue'
import apiSetting from '@/services/Setting'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import apiAuth from '@/services/auth'
import apiMst from '@/services/masterDate'

export default {
  components: {
    BRow,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BForm,
    BButton,
    BFormSelect,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isEditing: false,
      form: {
        empid: '',
        username: '',
        email: '',
        password: '',
        phone: '',
        role: null,
        jobtitle: null,
        dept: null,
      },
      isDrawing: false,
      lastX: 0,
      lastY: 0,
      roles: [],
      depts: [],
      jobtitles: [],
      profileImageFile: null,
      imageSrc: null,
      imageSrcsig: null,
      originalSignature: null,
      passwordFieldType: 'password',
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  mounted() {
    this.initialize()
    console.log('Employee Data:', this.employee)
    if (this.employee) {
      this.isEditing = true
      this.initializeForm(this.employee)

      this.originalSignature = this.employee.signature || null
      this.imageSrcsig = this.originalSignature

      if (this.imageSrcsig) {
        this.drawOldSignature() // วาดลายเซ็นเก่า
      }
    }
    this.fetchJobtitle()
  },

  methods: {
    async initialize() {
      await Promise.all([this.fetchRoles(), this.fetchDepts(), this.fetchJobtitle()])
    },
    initializeForm(employeeData) {
      this.form = {
        empid: employeeData.empid || '',
        username: employeeData.username || '',
        email: employeeData.email || '',
        password: '',
        phone: employeeData.phone || '',
        role: employeeData.roles.length ? employeeData.roles[0].id : null,
        jobtitle: this.jobtitles.length
          ? this.jobtitles.find(jobtitle => jobtitle.titleName === employeeData.jobtitle.titleName)?.id || null
          : null,
        dept: this.depts.find(dept => dept.dept === employeeData.dept)?.id || null,
      }
      this.imageSrc = employeeData.imageUrl || null
      this.imageSrcsig = employeeData.signature || null
    },
    async onSubmit() {
      const formData = new FormData()
      formData.append('empid', this.form.empid)
      formData.append('username', this.form.username)
      formData.append('email', this.form.email)
      // ตรวจสอบการเพิ่มหรือแก้ไข
      if (this.isEditing) {
        // หากแก้ไข, ส่งรหัสผ่านถ้ามีการเปลี่ยนแปลง
        if (this.form.password) {
          formData.append('password', this.form.password)
        }
        // ไม่ต้องเพิ่มรหัสผ่านถ้าไม่ได้เปลี่ยนแปลง
      } else {
        // หากเพิ่มใหม่ ต้องมีรหัสผ่าน
        formData.append('password', this.form.password)
      }
      formData.append('phone', this.form.phone)
      formData.append('role', this.form.role)
      formData.append('jobtitle', this.form.jobtitle)
      formData.append('deptId', this.form.dept)
      if (this.profileImageFile) {
        formData.append('image', this.profileImageFile)
      }
      if (this.isEditing) {
        if (this.imageSrcsig) {
          formData.append('signature', this.imageSrcsig) // ส่งลายเซ็นใหม่หากมี
        } else {
          formData.append('signature', this.originalSignature) // ใช้ลายเซ็นเดิมถ้าไม่มีการวาดใหม่
        }
      } else if (this.imageSrcsig) {
        formData.append('signature', this.imageSrcsig) // ส่งลายเซ็นใหม่หากเป็นการเพิ่ม
      }

      const endpoint = this.isEditing ? `auth/update/${this.employee.id}` : 'auth/register'

      try {
        const response = await apiAuth[this.isEditing ? 'put' : 'post'](endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        // Debugging: Log response
        console.log('Response:', response)

        if (response.status !== 200) {
          throw new Error('Operation failed')
        }

        this.$bvToast.toast('Operation completed successfully!', { title: 'Success', variant: 'success' })
        this.closeModalAndRefresh()
      } catch (error) {
        this.$bvToast.toast(`Error during operation: ${error.message}`, { title: 'Error', variant: 'danger' })
        console.error('Error during operation:', error)
      }
    },

    async fetchDepts() {
      try {
        const response = await apiSetting.get('/dept')
        this.depts = response.data || []
        console.log('Fetched departments:', this.depts)

        if (this.isEditing && this.employee && this.employee.dept) {
          const department = this.depts.find(dept => dept.dept === this.employee.dept)
          this.form.dept = department ? department.id : null
        }
      } catch (error) {
        console.error('Error fetching departments:', error)
      }
    },

    async fetchJobtitle() {
      try {
        const response = await apiMst.get('/jobtitleAll')
        this.jobtitles = response.data || []
        console.log('Fetched jobtitles:', this.jobtitles)

        if (this.isEditing && this.employee && this.employee.jobtitle) {
          const jbtitle = this.jobtitles.find(jobtitle => jobtitle.titleName === this.employee.jobtitle.titleName)
          this.form.jobtitle = jbtitle ? jbtitle.id : null
        }
      } catch (error) {
        console.error('Error fetching jobtitles:', error)
      }
    },

    async fetchRoles() {
      try {
        const response = await apiSetting.get('role')
        this.roles = response.data
      } catch (error) {
        console.error('Error fetching roles:', error)
      }
    },
    closeModalAndRefresh() {
      this.$emit('close')
      this.$emit('data-added')
    },
    onFileChange(event) {
      this.profileImageFile = event.target.files[0]
      this.imageSrc = this.profileImageFile ? URL.createObjectURL(this.profileImageFile) : null
    },
    onFileChangesig(event) {
      this.imageSrcsig = event.target.files[0]
      this.imageSrcsig = this.imageSrcsig ? URL.createObjectURL(this.imageSrcsig) : null
    },

    // signature
    startDrawing(event) {
      this.isDrawing = true
      const canvas = this.$refs.signatureCanvas
      const rect = canvas.getBoundingClientRect()
      this.lastX = event.offsetX !== undefined ? event.offsetX : event.touches[0].clientX - rect.left
      this.lastY = event.offsetY !== undefined ? event.offsetY : event.touches[0].clientY - rect.top
    },
    draw(event) {
      if (!this.isDrawing) return
      const canvas = this.$refs.signatureCanvas
      const ctx = canvas.getContext('2d')
      const rect = canvas.getBoundingClientRect()
      const x = event.offsetX !== undefined ? event.offsetX : event.touches[0].clientX - rect.left
      const y = event.offsetY !== undefined ? event.offsetY : event.touches[0].clientY - rect.top

      ctx.beginPath()
      ctx.moveTo(this.lastX, this.lastY)
      ctx.lineTo(x, y)
      ctx.strokeStyle = '#000'
      ctx.lineWidth = 2
      ctx.stroke()

      this.lastX = x
      this.lastY = y
    },
    stopDrawing() {
      this.isDrawing = false
      this.convertCanvasToImage()
    },
    clearCanvas(event) {
      event.preventDefault()
      const canvas = this.$refs.signatureCanvas
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      this.imageSrcsig = null // เคลียร์ค่าลายเซ็น
    },
    convertCanvasToImage() {
      const canvas = this.$refs.signatureCanvas
      this.imageSrcsig = canvas.toDataURL('image/png')
    },
    drawOldSignature() {
      const canvas = this.$refs.signatureCanvas
      const ctx = canvas.getContext('2d')
      const img = new Image()
      img.src = this.imageSrcsig

      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height) // เคลียร์ canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height) // วาดลายเซ็นเก่า
      }
    },

  },

}
</script>

<style scoped>
.auth-wrapper {
  padding: 20px;
}
.input-text {
  width: 21vw;            /* ความกว้างจะเป็น 21% ของความกว้างของ viewport */
  max-width: 500px;       /* จำกัดความกว้างสูงสุดไม่เกิน 500px */
  min-width: 50px;        /* จำกัดไม่ให้แคบเกิน 50px */
  box-sizing: border-box; /* ป้องกันไม่ให้ padding หรือ border เกินขนาดที่กำหนด */
}

/* สำหรับหน้าจอที่มีความกว้างไม่เกิน 768px (เช่น มือถือ) */
@media (max-width: 768px) {
  .input-text {
    width: 400px;        /* ให้ความกว้างของ input เท่ากับ 80% ของ viewport width */
    min-width: 150px;   /* จำกัดความกว้างขั้นต่ำเป็น 150px */
  }
  canvas {
  width: 340px; /* ป้องกันไม่ให้ canvas เกินขนาดของ container */
  height: auto; /* ทำให้ความสูงปรับตามสัดส่วน */
  }
}

/* สำหรับหน้าจอที่มีความกว้างไม่เกิน 480px (เช่น มือถือขนาดเล็ก) */
@media (max-width: 360px) {
  .input-text {
  max-width: 230px;       /* จำกัดความกว้างสูงสุดไม่เกิน 500px */
  min-width: 50px;        /* จำกัดไม่ให้แคบเกิน 50px */
  box-sizing: border-box; /* ป้องกันไม่ให้ padding หรือ border เกินขนาดที่กำหนด */
  }
  canvas {
  width: 230px;
  height: auto;
  }
}

canvas {
  max-width: 100%;
  height: auto;
}

</style>
